import { Controller } from "@hotwired/stimulus"
import { showHubTeamToast } from "../utils/hub_team_toast"

export default class extends Controller {
  static values = { url: String, field: String }

  connect() {
    this.previousValue = this.currentValue()
    this.reverting = false
    this.boundSave = this.save.bind(this)
    this.element.addEventListener("app-select:change", this.boundSave)
  }

  disconnect() {
    this.element.removeEventListener("app-select:change", this.boundSave)
  }

  async save(event) {
    if (this.reverting) return

    const value = event.detail?.value
    if (!this.hasUrlValue || !this.hasFieldValue) return
    if (Array.isArray(value)) {
      // A multi-select (a member's roles, TMS-1880) must keep at least one value.
      if (this.sameValue(value, this.previousValue)) return
      if (value.length === 0) {
        this.revert()
        showHubTeamToast(`Choose at least one ${this.fieldLabel().replace(/s$/, "")}.`, { variant: "error" })
        return
      }
    } else if (!value || value === this.previousValue) {
      return
    }

    this.setDisabled(true)

    const formData = new FormData()
    formData.append("_method", "PATCH")
    if (Array.isArray(value)) {
      value.forEach((item) => formData.append(`company_user[${this.fieldValue}][]`, item))
    } else {
      formData.append(`company_user[${this.fieldValue}]`, value)
    }

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "application/json"
        },
        credentials: "same-origin"
      })

      if (response.ok) {
        const data = await response.json()
        this.previousValue = data[this.fieldValue]
        this.updateSortData(data[this.fieldValue])
      } else {
        this.revert()
        const message = await this.errorMessage(response)
        showHubTeamToast(`Failed to update ${this.fieldLabel()}: ${message}`, { variant: "error" })
      }
    } catch (error) {
      console.error(`Error updating company user ${this.fieldValue}:`, error)
      this.revert()
      showHubTeamToast("An error occurred. Please try again.", { variant: "error" })
    } finally {
      this.setDisabled(false)
    }
  }

  revert() {
    if (!this.previousValue) return

    if (Array.isArray(this.previousValue)) {
      this.selectController?.restoreMultipleSelection(this.previousValue)
      return
    }

    const option = this.element.querySelector(
      `[data-app-select-target="option"][data-value="${this.previousValue}"]`
    )
    if (!option) return

    this.reverting = true
    option.click()
    this.reverting = false
  }

  updateSortData(value) {
    const cell = this.element.closest("td")
    if (!cell) return

    const values = Array.isArray(value) ? value : [value]
    cell.dataset.sortValue = values.join(" ")
    cell.dataset.filterValue = values.join(" ").toLowerCase()
  }

  // Read from the markup rather than the app-select controller, which has not connected yet when
  // this one does.
  currentValue() {
    if (this.element.querySelector('[data-app-select-multiple-value="true"]')) {
      return Array.from(this.element.querySelectorAll(".select-hidden-input-value"))
        .map((input) => input.value)
        .filter((value) => value !== "")
    }

    return this.hiddenInput?.value
  }

  sameValue(values, previous) {
    if (!Array.isArray(previous) || values.length !== previous.length) return false

    return values.every((item) => previous.includes(item))
  }

  get selectController() {
    const element = this.element.querySelector('[data-controller~="app-select"]')
    return element && this.application.getControllerForElementAndIdentifier(element, "app-select")
  }

  setDisabled(disabled) {
    const button = this.element.querySelector(".app-select-button")
    if (button) button.disabled = disabled
  }

  fieldLabel() {
    return this.fieldValue.replace(/_/g, " ")
  }

  get hiddenInput() {
    return this.element.querySelector(".select-hidden-input-value")
  }

  async errorMessage(response) {
    try {
      const data = await response.json()
      if (data?.errors?.length) return data.errors.join(", ")
    } catch (_) {
      // fall through
    }
    return `HTTP ${response.status}`
  }
}
